import {Component, ElementRef, OnInit, ViewChild, Input} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {Country} from "../../../shared/models/country";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {FormBuilder, Validators} from "@angular/forms";
import {CountryListValidator} from "./country-list-validator";
import {ValidationDetail} from "../../../shared/models/ValidationDetail";
import {MatChipInputEvent} from "@angular/material/chips";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {MatRadioChange} from "@angular/material/radio";
import {map, startWith, takeUntil} from "rxjs/operators";
import {CreateApplicationService} from "../create-application.service";
import {FacadeService} from "../../../shared/services/facade.service";
import {ApplicationService} from "../../application.service";
import {UtilitiesService} from "../../../core/services/utilities.service";
import {ApplicationLogDetailsRequest, ApplicationLogStepRequest, Log, Step, ApplicationProductRequest} from "leia-api";
import NameEnum = Step.NameEnum;
import StateEnum = Step.StateEnum;
import {orderForOptions} from "./order-for-options";
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;

@Component({
  selector: 'app-bsa-profile',
  templateUrl: './bsa-profile.component.html',
  styleUrls: ['./bsa-profile.component.css']
})
export class BsaProfileComponent implements OnInit {
  @ViewChild('countryInput') countryInput!: ElementRef;
  @Input() hasCoApplicant = false;
  @Input() displayCheckOrder?: boolean;
  @Input() displayDebitOrder?: boolean;

  filteredCountries?: Observable<Country[]>;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  maxNumberOfCountries: number = 5;
  wireTransferInternational?: boolean;
  accountValidationDetail: ValidationDetail;
  countryList: Country[] = [];
  removable = true;
  destroySubject$: Subject<void> = new Subject();

  //form for account details
  bsaProfileForm = this.formBuilder.group({
    achTransactions: ['', [Validators.required]],
    wireDomestic: ['', [Validators.required]],
    wireInternational: ['', [Validators.required]],
    cashTransactions: ['', [Validators.required]],
    useDebitCard: ['', [Validators.required]],
    orderDebitCard: [''],
    debitCardOrderFor: [0, [Validators.required]],
    useChecks: ['', [Validators.required]],
    orderChecks: [''],
    checksOrderFor: [1, [Validators.required]],
    internationalCountry: [''],
    countryInput: [null],
  }, {validators: [this.bsaCountryListValidator.checkCountryListIfNecessary()]});

  orderForOptions = orderForOptions;

  get isDebitCardDetailsVisible(): boolean {
    return this.getFormControlValue("useDebitCard");
  };

  get isDebitCardForVisible(): boolean {
    if (!this.hasCoApplicant) {
      return false;
    }

    return !!this.getFormControlValue("orderDebitCard");
  };

  get isChecksDetailsVisible(): boolean {
    return this.getFormControlValue("useChecks");
  };

  get isChecksForVisible(): boolean {
    if (!this.hasCoApplicant) {
      return false;
    }

    return !!this.getFormControlValue("orderChecks");
  };

  constructor(private formBuilder: FormBuilder,
              private bsaCountryListValidator: CountryListValidator,
              private createApplicationService: CreateApplicationService,
              private facadeService: FacadeService,
              private applicationService: ApplicationService,
              private utils: UtilitiesService) {
    this.accountValidationDetail = new ValidationDetail(this.bsaProfileForm, new Map<string, string>());
  }

  ngOnInit(): void {
    this.populateLists();
    this.setupValidationMap();
    this.setupStatusTracker();
    this.setupValidationTrigger();
    this.bsaProfileForm.valueChanges.pipe(takeUntil(this.destroySubject$)).subscribe(value => {
      this.createApplicationService.formElementHasChanged = true;
    });

    this._setupDebitCardValidatorEvents();
    this._setupOrderCheckValidatorEvents();

  }

  private _setupDebitCardValidatorEvents(): void {
    // Listen for value changes on use debit card field, order debit card field needs to be required if
    // use debit card is true.
    this.bsaProfileForm.get("useDebitCard")?.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {

        //determine value based on the config setting orderDebit value as well.
        //if the config setting value is true, selected radio value is true then validate
        //else validation should be false.
        const needsValidation = value && this.displayDebitOrder;

        this._setOrderValidation("orderDebitCard", value);
        const orderChecksValue = (value === true && this.displayDebitOrder === true) ? null : false;
        this.bsaProfileForm.get("orderDebitCard")!.setValue(orderChecksValue);
      });

    // Listen for value changes on order debit card field, if order debit card is true then default value
    // for debit card order for field needs to be "1, primary", if false then "0, none".
    this.bsaProfileForm.get("orderDebitCard")?.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        let newValue = 0;

        if (value) {
          newValue = 1;
        }

        this.bsaProfileForm.get("debitCardOrderFor")!.patchValue(newValue);
      });

  }

  private _setupOrderCheckValidatorEvents(): void {
    this.bsaProfileForm.get("useChecks")?.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        //determine value based on the config setting orderCheck value as well.
        //if the config setting value is true, selected radio value is true then validate
        //else validation should be false.
        const needsValidation = value && this.displayCheckOrder;

        this._setOrderValidation("orderChecks", needsValidation);
        // When useChecks becomes true, set orderChecks to false
        // Set orderChecks value based on conditions
        const orderChecksValue = (value === true && this.displayCheckOrder === true) ? null : false;
        this.bsaProfileForm.get("orderChecks")!.setValue(orderChecksValue);
      });

    this.bsaProfileForm.get("orderChecks")?.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => {
        let newValue = 0;

        if (value) {
          newValue = 1;
        }

        this.bsaProfileForm.get("checksOrderFor")!.patchValue(newValue);
      });

  }

  private _setOrderValidation(formControlName: string, required: boolean): void {
    const control = this.bsaProfileForm.get(formControlName);
    const validators = [];
    if (required) {
      validators.push(Validators.required);
    }

    control?.setValidators(validators);
    control?.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.clearValidations();
    this.destroySubject$.next();
  }

  clearValidations(): void {
    this.createApplicationService.validationsChange.emit(
      new ValidationDetail(this.bsaProfileForm, this.accountValidationDetail.labels, true)
    );
  }

  /**
   * Setup validation map for accounts.
   */
  setupValidationMap(): void {
    let labelMap = new Map<string, string>();
    labelMap.set("achTransactions", "ACH Transactions");
    labelMap.set("wireDomestic", "Wire Domestic");
    labelMap.set("wireInternational", "Wire International");
    labelMap.set("internationalCountry", "No International Countries Selected");
    labelMap.set("cashTransactions", "Cash Transactions");
    labelMap.set("useDebitCard", "Use Debit Card");
    labelMap.set("useChecks", "Use Checks");
    this.accountValidationDetail = new ValidationDetail(this.bsaProfileForm, labelMap);
  }

  /***
   * Sets up the tracker validation states for this component
   */
  setupStatusTracker(): void {
    this.bsaProfileForm.statusChanges.pipe(takeUntil(this.destroySubject$)).subscribe(state => {
      this.updateValidations();
      setTimeout(() => {
        if (state === "VALID") {
          this.bsaProfileComplete();
        }
      });
    });
  }


  bsaProfileComplete() {
    const applicationRequest = this.applicationService.applicationLogsRequest;
    const onboardingRequest = this.applicationService.onboardingRequest;
    let log = new Log();
    log.source = "Coruscant";
    log.timestamp = new Date();
    log.details = "BSA Profile section is complete.";
    applicationRequest.step = this.utils.createStep(NameEnum.AccountDetails, StateEnum.Success, [log]);
    applicationRequest.applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.ACCOUNTDETAILS, OnboardingStateEnum.SUCCESS, OnboardingSourceEnum.CORUSCANT, [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.CORUSCANT, "BSA Profile section is complete.")]);

    const {
      achTransactions,
      wireDomestic,
      wireInternational,
      cashTransactions,
      useDebitCard,
      orderDebitCard,
      debitCardOrderFor,
      useChecks,
      orderChecks,
      checksOrderFor
    } = this.bsaProfileForm.value;
    const selectedCountries = this.getSelectedCountryList();
    const countryList = selectedCountries.map(country => country.countryName).join(',');

    let applicationProductRequest = (applicationRequest.products! && applicationRequest.products?.length > 0) ? applicationRequest.products![0] : new ApplicationProductRequest();

    applicationRequest.products = [];

    applicationProductRequest.achTransactions = achTransactions;
    applicationProductRequest.wireTransferDomestic = wireDomestic;
    applicationProductRequest.wireTransferInternational = wireInternational;
    applicationProductRequest.wireTransferInternationalCountries = countryList;
    applicationProductRequest.cashTransactions = cashTransactions;
    applicationProductRequest.usingChecks = useChecks;
    applicationProductRequest.usingDebitCard = useDebitCard;
    applicationProductRequest.orderChecks = orderChecks;
    applicationProductRequest.orderDebitCard = orderDebitCard;
    applicationProductRequest.orderChecksApplicantType = checksOrderFor;
    applicationProductRequest.orderDebitApplicantType = debitCardOrderFor;

    applicationRequest.products.push(applicationProductRequest);

    this.applicationService.applicationLogsRequest = applicationRequest;
    this.applicationService.onboardingRequest = onboardingRequest;
    this.applicationService.bsaProfileStatus = true;
  }

  setupValidationTrigger(): void {
    this.createApplicationService.onTriggerValidations.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.bsaProfileForm.markAllAsTouched();
      this.bsaProfileForm.updateValueAndValidity();
    });
  };

  updateValidations(): void {
    this.createApplicationService.validationsChange.emit(this.accountValidationDetail);
  }

  getFormControlValue(name: string): any {
    return this.bsaProfileForm.get(name)?.value;
  }

  /***
   * populate the list values
   */

  populateLists(): void {
    this.getCountryList();
  }

  getSelectedCountryList(): Country[] {
    const {internationalCountry} = this.bsaProfileForm.value;
    return !Array.isArray(internationalCountry)
      ? (!!internationalCountry ? [internationalCountry] : [])
      : internationalCountry;
  }


  /***
   * get the available country list, currently mocked due to not being available in Falcon
   */
  getCountryList(): void {
    // only show international and non-ofac sanctioned countries
    this.facadeService.getCountries().then(countries => {
      this.countryList = countries.filter(country => country.isInternational);
      // International Country Auto-complete filter
      this.filterCountries();
    });
  }

  filterCountries(): void {
    this.filteredCountries = this.bsaProfileForm.controls['countryInput'].valueChanges.pipe(
      startWith(null),
      map(id => id ? this._filter(id) : this.countryList.slice(),
      ));
  }

  /***
   *
   * @param countryId
   */
  remove(countryId: number): void {
    let country = this.countryList.find(o => o.countryId === countryId);

    if (country !== undefined) {
      const {internationalCountry} = this.bsaProfileForm.controls;
      internationalCountry.value.splice(
        internationalCountry.value.findIndex((value: Country) => country!.countryId === value.countryId),
        1
      );
      internationalCountry.setValue(internationalCountry.value);
      internationalCountry.updateValueAndValidity();
      this.filterCountries();
    }
  }

  /***
   *
   * @param event
   */
  selected(event: MatAutocompleteSelectedEvent): void {
    let selectedCountry = this.countryList.find(o => o.countryId === event.option.value.countryId);
    if (selectedCountry !== undefined) {
      const selectedCountryList = this.getSelectedCountryList();

      if (
        selectedCountryList.indexOf(selectedCountry) < 0 &&
        selectedCountryList.length < this.maxNumberOfCountries
      ) {
        const {internationalCountry, countryInput} = this.bsaProfileForm.controls;
        selectedCountryList.push(selectedCountry);

        this.countryInput.nativeElement.value = "";
        countryInput.setValue(null);
        countryInput.updateValueAndValidity();
        internationalCountry.setValue(selectedCountryList);
        internationalCountry.updateValueAndValidity();
        this.filterCountries();
      }
    }
  }

  /***
   *
   * @param event
   */
  internationalWireChange(event: MatRadioChange): void {
    this.wireTransferInternational = event.value;
  }

  tokenAdded(event: MatChipInputEvent): void {
    if (!event.chipInput) {
      return;
    }
    event.chipInput.inputElement.value = "";
  }

  /***
   *
   * @param value
   * @private
   */
  private _filter(value: string): Country[] {
    // filter the country list - autocomplete
    let filteredValues = this.countryList.filter(option => option.countryName.toLowerCase().indexOf(value.toLowerCase()) !== -1);
    return filteredValues;
  }
}
